import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  AreaChart,
  Area,
  Legend,
} from "recharts";
import { Link } from "react-router-dom";

interface Campaign {
  id: string;
  name: string;
  cost: number;
  impressions: number;
  clicks: number;
  ctr: number;
  conversions: number;
  conversionValue: number;
  cpa: number;
  roas: number;
}

interface DestinationAds {
  destination: string;
  destinationId: string | null;
  destinationSlug: string;
  totalCost: number;
  totalConversions: number;
  totalRevenue: number;
  roas: number;
  cpa: number;
  profitable: boolean;
}

interface AdsSummary {
  totalSpend: number;
  totalConversions: number;
  totalRevenue: number;
  overallRoas: number;
  avgCpa: number;
}

interface MonthlyAds {
  month: string;
  spend: number;
  conversions: number;
  revenue: number;
}

interface Recommendation {
  destination: string;
  destinationId: string | null;
  issue: string;
  suggestion: string;
  priority: "high" | "medium" | "low";
}

interface AdsPerformanceProps {
  campaigns: Campaign[];
  byDestination: DestinationAds[];
  summary: AdsSummary;
  byMonth: MonthlyAds[];
  recommendations: Recommendation[];
  isLoading?: boolean;
  error?: string | null;
}

import { formatMonth, formatCompactNumber } from ".";

function getRoasColor(roas: number): string {
  if (roas >= 5) return "#10b981"; // green
  if (roas >= 2) return "#3b82f6"; // blue
  if (roas >= 1) return "#f59e0b"; // yellow
  return "#ef4444"; // red
}

export default function AdsPerformance({
  campaigns,
  byDestination,
  summary,
  byMonth,
  recommendations,
  isLoading,
  error,
}: AdsPerformanceProps) {
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="card animate-pulse">
          <div className="mb-4 h-6 w-48 rounded bg-gray-200 dark:bg-gray-700" />
          <div className="h-64 rounded bg-gray-100 dark:bg-gray-800" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card">
        <div className="py-8 text-center">
          <p className="text-red-500 dark:text-red-400">{error}</p>
          <p className="mt-2 text-sm text-gray-500">
            Check Google Ads settings in environment variables.
          </p>
        </div>
      </div>
    );
  }

  const sortedByRoas = [...byDestination].sort((a, b) => b.roas - a.roas);

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-5">
        <div className="card">
          <p className="text-sm text-gray-500 dark:text-gray-400">Total Spend</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            ${summary.totalSpend.toLocaleString("en-US")}
          </p>
        </div>
        <div className="card">
          <p className="text-sm text-gray-500 dark:text-gray-400">Ad Revenue</p>
          <p className="text-2xl font-bold text-green-600 dark:text-green-400">
            ${summary.totalRevenue.toLocaleString("en-US")}
          </p>
        </div>
        <div className="card">
          <p className="text-sm text-gray-500 dark:text-gray-400">ROAS</p>
          <p
            className={`text-2xl font-bold ${
              summary.overallRoas >= 1
                ? "text-green-600 dark:text-green-400"
                : "text-red-600 dark:text-red-400"
            }`}
          >
            {summary.overallRoas.toFixed(1)}x
          </p>
        </div>
        <div className="card">
          <p className="text-sm text-gray-500 dark:text-gray-400">Conversions</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {summary.totalConversions.toLocaleString("en-US")}
          </p>
        </div>
        <div className="card">
          <p className="text-sm text-gray-500 dark:text-gray-400">Avg. CPA</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            ${summary.avgCpa.toLocaleString("en-US")}
          </p>
        </div>
      </div>

      {/* Recommendations */}
      {recommendations.length > 0 && (
        <div className="card">
          <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
            Recommendations
          </h3>
          <div className="space-y-3">
            {recommendations.map((rec, index) => (
              <div
                key={index}
                className={`rounded-lg border p-3 ${
                  rec.priority === "high"
                    ? "border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20"
                    : rec.priority === "medium"
                      ? "border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-900/20"
                      : "border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-900/20"
                }`}
              >
                <div className="flex items-start gap-3">
                  <span
                    className={`text-lg ${
                      rec.priority === "high"
                        ? "text-red-500"
                        : rec.priority === "medium"
                          ? "text-yellow-500"
                          : "text-blue-500"
                    }`}
                  >
                    {rec.issue.includes("High ROAS") ? "+" : rec.issue.includes("Low") ? "!" : "i"}
                  </span>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {rec.destinationId ? (
                        <Link
                          to={`/destinations/${rec.destinationId}`}
                          className="text-blue-600 hover:text-blue-800 dark:text-blue-400"
                        >
                          {rec.destination}
                        </Link>
                      ) : (
                        rec.destination
                      )}
                      : {rec.issue}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{rec.suggestion}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ROAS by Destination Chart */}
      <div className="card">
        <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
          ROAS by Destination
        </h3>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={sortedByRoas}
              layout="vertical"
              margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                type="number"
                tick={{ fontSize: 11, fill: "#6b7280" }}
                tickFormatter={(v) => `${v}x`}
              />
              <YAxis
                type="category"
                dataKey="destination"
                tick={{ fontSize: 11, fill: "#6b7280" }}
                width={95}
              />
              <Tooltip
                formatter={(value: number) => [`${value.toFixed(1)}x`, "ROAS"]}
                contentStyle={{
                  backgroundColor: "#fff",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                }}
              />
              <Bar dataKey="roas" radius={[0, 4, 4, 0]}>
                {sortedByRoas.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={getRoasColor(entry.roas)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-4 flex items-center justify-center gap-6 text-sm">
          <span className="flex items-center gap-2">
            <span className="h-3 w-3 rounded bg-green-500" /> ROAS 5+
          </span>
          <span className="flex items-center gap-2">
            <span className="h-3 w-3 rounded bg-blue-500" /> ROAS 2-5
          </span>
          <span className="flex items-center gap-2">
            <span className="h-3 w-3 rounded bg-yellow-500" /> ROAS 1-2
          </span>
          <span className="flex items-center gap-2">
            <span className="h-3 w-3 rounded bg-red-500" /> ROAS &lt;1 (loss)
          </span>
        </div>
      </div>

      {/* Spend vs Revenue Trend */}
      <div className="card">
        <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
          Spend vs. Revenue Over Time
        </h3>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={byMonth} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="colorSpend" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="month"
                tickFormatter={formatMonth}
                tick={{ fontSize: 12, fill: "#6b7280" }}
                axisLine={{ stroke: "#e5e7eb" }}
              />
              <YAxis
                tickFormatter={formatCompactNumber}
                tick={{ fontSize: 12, fill: "#6b7280" }}
                axisLine={{ stroke: "#e5e7eb" }}
                width={60}
              />
              <Tooltip
                formatter={(value: number, name: string) => [
                  `$${value.toLocaleString("en-US")}`,
                  name === "spend" ? "Spend" : "Revenue",
                ]}
                labelFormatter={formatMonth}
                contentStyle={{
                  backgroundColor: "#fff",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                }}
              />
              <Legend formatter={(value) => (value === "spend" ? "Spend" : "Revenue")} />
              <Area
                type="monotone"
                dataKey="spend"
                name="spend"
                stroke="#ef4444"
                strokeWidth={2}
                fill="url(#colorSpend)"
              />
              <Area
                type="monotone"
                dataKey="revenue"
                name="revenue"
                stroke="#10b981"
                strokeWidth={2}
                fill="url(#colorRevenue)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Destinations Table */}
      <div className="card">
        <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
          Performance by Destination
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="py-3 text-left font-medium text-gray-500 dark:text-gray-400">
                  Destination
                </th>
                <th className="py-3 text-right font-medium text-gray-500 dark:text-gray-400">
                  Cost
                </th>
                <th className="py-3 text-right font-medium text-gray-500 dark:text-gray-400">
                  Revenue
                </th>
                <th className="py-3 text-right font-medium text-gray-500 dark:text-gray-400">
                  Conversions
                </th>
                <th className="py-3 text-right font-medium text-gray-500 dark:text-gray-400">
                  CPA
                </th>
                <th className="py-3 text-right font-medium text-gray-500 dark:text-gray-400">
                  ROAS
                </th>
                <th className="py-3 text-center font-medium text-gray-500 dark:text-gray-400">
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              {byDestination.map((dest) => (
                <tr
                  key={dest.destinationSlug}
                  className="border-b border-gray-100 hover:bg-gray-50 dark:border-gray-800 dark:hover:bg-gray-800/50"
                >
                  <td className="py-3">
                    {dest.destinationId ? (
                      <Link
                        to={`/destinations/${dest.destinationId}`}
                        className="text-blue-600 hover:text-blue-800 dark:text-blue-400"
                      >
                        {dest.destination}
                      </Link>
                    ) : (
                      <span className="text-gray-900 dark:text-white">{dest.destination}</span>
                    )}
                  </td>
                  <td className="py-3 text-right text-gray-900 dark:text-white">
                    ${dest.totalCost.toLocaleString("en-US")}
                  </td>
                  <td className="py-3 text-right font-medium text-green-600 dark:text-green-400">
                    ${dest.totalRevenue.toLocaleString("en-US")}
                  </td>
                  <td className="py-3 text-right text-gray-600 dark:text-gray-400">
                    {dest.totalConversions.toLocaleString("en-US")}
                  </td>
                  <td className="py-3 text-right text-gray-600 dark:text-gray-400">
                    ${dest.cpa.toLocaleString("en-US")}
                  </td>
                  <td className="py-3 text-right">
                    <span
                      className={`font-medium ${
                        dest.roas >= 2
                          ? "text-green-600 dark:text-green-400"
                          : dest.roas >= 1
                            ? "text-yellow-600 dark:text-yellow-400"
                            : "text-red-600 dark:text-red-400"
                      }`}
                    >
                      {dest.roas.toFixed(1)}x
                    </span>
                  </td>
                  <td className="py-3 text-center">
                    <span
                      className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${
                        dest.profitable
                          ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                          : "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
                      }`}
                    >
                      {dest.profitable ? "Profitable" : "Loss"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Campaigns Table */}
      <div className="card">
        <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">Campaigns</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="py-3 text-left font-medium text-gray-500 dark:text-gray-400">
                  Campaign
                </th>
                <th className="py-3 text-right font-medium text-gray-500 dark:text-gray-400">
                  Cost
                </th>
                <th className="py-3 text-right font-medium text-gray-500 dark:text-gray-400">
                  Impressions
                </th>
                <th className="py-3 text-right font-medium text-gray-500 dark:text-gray-400">
                  Clicks
                </th>
                <th className="py-3 text-right font-medium text-gray-500 dark:text-gray-400">
                  CTR
                </th>
                <th className="py-3 text-right font-medium text-gray-500 dark:text-gray-400">
                  Conversions
                </th>
                <th className="py-3 text-right font-medium text-gray-500 dark:text-gray-400">
                  ROAS
                </th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map((campaign) => (
                <tr
                  key={campaign.id}
                  className="border-b border-gray-100 hover:bg-gray-50 dark:border-gray-800 dark:hover:bg-gray-800/50"
                >
                  <td className="py-3 text-gray-900 dark:text-white">{campaign.name}</td>
                  <td className="py-3 text-right text-gray-900 dark:text-white">
                    ${campaign.cost.toLocaleString("en-US")}
                  </td>
                  <td className="py-3 text-right text-gray-600 dark:text-gray-400">
                    {campaign.impressions.toLocaleString("en-US")}
                  </td>
                  <td className="py-3 text-right text-gray-600 dark:text-gray-400">
                    {campaign.clicks.toLocaleString("en-US")}
                  </td>
                  <td className="py-3 text-right text-gray-600 dark:text-gray-400">
                    {campaign.ctr.toFixed(2)}%
                  </td>
                  <td className="py-3 text-right text-gray-600 dark:text-gray-400">
                    {campaign.conversions}
                  </td>
                  <td className="py-3 text-right">
                    <span
                      className={`font-medium ${
                        campaign.roas >= 2
                          ? "text-green-600 dark:text-green-400"
                          : campaign.roas >= 1
                            ? "text-yellow-600 dark:text-yellow-400"
                            : "text-red-600 dark:text-red-400"
                      }`}
                    >
                      {campaign.roas.toFixed(1)}x
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
